/**
 * Understanding Your Values v0.2 — disgust-first discovery.
 *
 * Instead of picking aspirational words from a long list, start with
 * experiences that evoked strong disgust. The opposite of the violation
 * is often a value you already live by.
 */

export type DisgustIntensity = 1 | 2 | 3 | 4 | 5;

export interface DisgustExperience {
  id: string;
  when: string;
  context: string;
  whatHappened: string;
  intensity: DisgustIntensity;
  violation: string;
  oppositeValue: string;
  oppositeInPractice: string;
}

export interface CoreValue {
  id: string;
  label: string;
  definition: string;
  sourceExperienceIds: string[];
}

export interface ValuesFromDisgustState {
  experiences: DisgustExperience[];
  coreValues: CoreValue[];
  completedAt?: string;
}

export type ValuesFromDisgustStep =
  | 'intro'
  | 'recall'
  | 'violation'
  | 'flip'
  | 'distill'
  | 'results';

export const VALUES_FROM_DISGUST_STEPS: {
  id: ValuesFromDisgustStep;
  label: string;
  short: string;
}[] = [
  { id: 'intro', label: 'Why disgust?', short: 'Start' },
  { id: 'recall', label: 'Recall', short: 'Experiences' },
  { id: 'violation', label: 'Name it', short: 'Violation' },
  { id: 'flip', label: 'Flip it', short: 'Opposite' },
  { id: 'distill', label: 'Distill', short: 'Top 5' },
  { id: 'results', label: 'Your values', short: 'Done' },
];

export const MIN_EXPERIENCES = 2;
export const TARGET_EXPERIENCES = 3;
export const MAX_CORE_VALUES = 5;

export const INTENSITY_LABELS: Record<DisgustIntensity, string> = {
  1: 'Mild unease',
  2: 'Uncomfortable',
  3: 'Strong revulsion',
  4: 'Visceral disgust',
  5: 'Still angry years later',
};

const OPPOSITE_HINTS: { pattern: RegExp; values: string[] }[] = [
  { pattern: /humiliat|shame|belittl|mock|ridicul/i, values: ['Dignity', 'Respect', 'Psychological safety'] },
  { pattern: /lie|dishonest|deceiv|mislead|cover.?up|gaslight/i, values: ['Integrity', 'Transparency', 'Honesty'] },
  { pattern: /credit|steal|plagiar|take.?my/i, values: ['Fair recognition', 'Ownership', 'Credit where due'] },
  { pattern: /cruel|mean|bully|abusive|hostile/i, values: ['Compassion', 'Care', 'Humanity'] },
  { pattern: /chaos|confus|unclear|ambiguous|contradict/i, values: ['Clarity', 'Coherence', 'Straight talk'] },
  { pattern: /micromanage|control|meddl|second.?guess/i, values: ['Autonomy', 'Trust', 'Empowerment'] },
  { pattern: /waste|careless|sloppy|reckless/i, values: ['Care', 'Craft', 'Responsibility'] },
  { pattern: /indifferen|apathy|neglect|ignore/i, values: ['Care', 'Ownership', 'Presence'] },
  { pattern: /arrogant|condescend|superior|entitled/i, values: ['Humility', 'Collaboration', 'Curiosity'] },
  { pattern: /exploit|unfair|inequit|favoritism|nepotism/i, values: ['Fairness', 'Equity', 'Just process'] },
  { pattern: /blame|scapegoat|throw.?under/i, values: ['Accountability', 'Psychological safety', 'Shared truth'] },
  { pattern: /token|performative|virtue.?signal|hollow/i, values: ['Authenticity', 'Substance over theater', 'Real commitment'] },
  { pattern: /silence|complicit|look.?away|enable/i, values: ['Courage', 'Moral courage', 'Speaking up'] },
  { pattern: /speed|rush|cut.?corner|shortcut/i, values: ['Quality', 'Thoughtfulness', 'Doing it right'] },
  { pattern: /loyalty.?test|politic|tribal|clique/i, values: ['Merit', 'Open debate', 'Substance over politics'] },
];

export function createExperience(): DisgustExperience {
  return {
    id: crypto.randomUUID(),
    when: '',
    context: '',
    whatHappened: '',
    intensity: 4,
    violation: '',
    oppositeValue: '',
    oppositeInPractice: '',
  };
}

export function createCoreValue(label = '', sourceExperienceIds: string[] = []): CoreValue {
  return {
    id: crypto.randomUUID(),
    label,
    definition: '',
    sourceExperienceIds,
  };
}

export function suggestOppositeValues(violation: string): string[] {
  const trimmed = violation.trim();
  if (!trimmed) return ['Integrity', 'Respect', 'Fairness', 'Autonomy', 'Care'];

  const suggestions = new Set<string>();
  for (const hint of OPPOSITE_HINTS) {
    if (hint.pattern.test(trimmed)) {
      hint.values.forEach(v => suggestions.add(v));
    }
  }

  if (suggestions.size === 0) {
    return ['Integrity', 'Respect', 'Fairness', 'Autonomy', 'Care'];
  }

  return Array.from(suggestions).slice(0, 5);
}

export function experiencesReadyForViolation(experiences: DisgustExperience[]): boolean {
  return (
    experiences.length >= MIN_EXPERIENCES &&
    experiences.every(e => e.when.trim() && e.context.trim() && e.whatHappened.trim())
  );
}

export function experiencesReadyForFlip(experiences: DisgustExperience[]): boolean {
  return experiences.length >= MIN_EXPERIENCES && experiences.every(e => e.violation.trim());
}

export function experiencesReadyForDistill(experiences: DisgustExperience[]): boolean {
  return experiences.length >= MIN_EXPERIENCES && experiences.every(e => e.oppositeValue.trim());
}

export function clusterOppositeValues(experiences: DisgustExperience[]): CoreValue[] {
  const buckets = new Map<string, string[]>();

  for (const exp of experiences) {
    const key = exp.oppositeValue.trim().toLowerCase();
    if (!key) continue;
    const ids = buckets.get(key) ?? [];
    ids.push(exp.id);
    buckets.set(key, ids);
  }

  return Array.from(buckets.entries())
    .map(([label, sourceExperienceIds]) =>
      createCoreValue(
        label.replace(/\b\w/g, c => c.toUpperCase()),
        sourceExperienceIds,
      ),
    )
    .sort((a, b) => b.sourceExperienceIds.length - a.sourceExperienceIds.length);
}

export function canAdvance(
  step: ValuesFromDisgustStep,
  state: ValuesFromDisgustState,
): boolean {
  switch (step) {
    case 'intro':
      return true;
    case 'recall':
      return experiencesReadyForViolation(state.experiences);
    case 'violation':
      return experiencesReadyForFlip(state.experiences);
    case 'flip':
      return experiencesReadyForDistill(state.experiences);
    case 'distill':
      return (
        state.coreValues.length > 0 &&
        state.coreValues.length <= MAX_CORE_VALUES &&
        state.coreValues.every(v => v.label.trim())
      );
    case 'results':
      return true;
    default:
      return false;
  }
}

export function stepIndex(step: ValuesFromDisgustStep): number {
  return VALUES_FROM_DISGUST_STEPS.findIndex(s => s.id === step);
}

export function nextStep(step: ValuesFromDisgustStep): ValuesFromDisgustStep | null {
  const idx = stepIndex(step);
  if (idx < 0 || idx >= VALUES_FROM_DISGUST_STEPS.length - 1) return null;
  return VALUES_FROM_DISGUST_STEPS[idx + 1].id;
}

export function prevStep(step: ValuesFromDisgustStep): ValuesFromDisgustStep | null {
  const idx = stepIndex(step);
  if (idx <= 0) return null;
  return VALUES_FROM_DISGUST_STEPS[idx - 1].id;
}
